import React, { useState, useEffect } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { getFileUrlAndType, isImageType, isPdfType } from '../utils/fileUtils';

function PassportViewer({ 
  registrationId, 
  accessCode, 
  onDownload = null, 
  height = "384px",
  className = "",
  onRetry = null
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreviewData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { url, contentType } = await getFileUrlAndType(registrationId, accessCode);
        
        setPreviewUrl(url);
        setContentType(contentType);
      } catch (e) {
        console.error('Failed to load passport preview:', e);
        setError(`Failed to load passport: ${e.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (registrationId && accessCode) {
      fetchPreviewData();
    }
  }, [registrationId, accessCode]);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Reset states and retry loading
      setError(null);
      setPreviewUrl(null);
      setContentType(null);
      // The useEffect will trigger a reload
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="w-full h-[600px] border border-gray-300 rounded bg-gray-50 flex items-center justify-center">
          <div className="text-center p-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading passport document...</p>
          </div>
        </div>
      );
    }

    if (error || !previewUrl) {
      return (
        <div className="w-full h-[600px] border border-gray-300 rounded bg-red-50 flex items-center justify-center">
          <div className="text-center p-6">
            <div className="text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Failed to Load</h3>
            <p className="text-red-600 mb-4">{error || 'Preview unavailable'}</p>
            <div className="space-y-2">
              <button
                onClick={handleRetry}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded mr-2"
              >
                Try Again
              </button>
              {onDownload && (
                <button
                  onClick={onDownload}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                >
                  Download Instead
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    const isImage = isImageType(contentType);
    const isPdf = isPdfType(contentType);

    if (isImage) {
      if (contentType === 'image/heic') {
        return (
          <div className="w-full h-[600px] border border-gray-300 rounded bg-yellow-50 flex items-center justify-center">
            <div className="text-center p-6">
              <div className="text-6xl mb-4">📱</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                HEIC Image Format
              </h3>
              <p className="text-gray-600 mb-4">
                This is an Apple HEIC image format that cannot be displayed directly in the browser.
              </p>
              {onDownload && (
                <button
                  onClick={onDownload}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download to View
                </button>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Open with Apple Photos, Preview, or other HEIC-compatible applications
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="w-full h-[600px] border border-gray-300 rounded bg-gray-100">
          <TransformWrapper
            initialScale={0.8}
            minScale={0.2}
            maxScale={4}
            wheel={{ step: 0.1 }}
            centerOnInit={true}
            limitToBounds={false}
            centerZoomedOut={true}
          >
            {({ zoomIn, zoomOut, resetTransform, centerView, state }) => (
              <>
                {/* Controls */}
                <div className="flex justify-center space-x-2 mb-2 p-2 bg-gray-50 rounded-t">
                  <button
                    onClick={() => zoomIn()}
                    className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded"
                    title="Zoom In"
                  >
                    🔍+
                  </button>
                  <button
                    onClick={() => zoomOut()}
                    className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded"
                    title="Zoom Out"
                  >
                    🔍-
                  </button>
                  <button
                    onClick={() => resetTransform()}
                    className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded"
                    title="Reset"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => centerView()}
                    className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded"
                    title="Center"
                  >
                    Center
                  </button>
                  <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                    {Math.round((state?.scale || 1) * 100)}%
                  </span>
                </div>
                <TransformComponent>
                  <img
                    src={previewUrl}
                    alt="Passport Document"
                    className="max-w-none"
                    style={{
                      display: 'block',
                      maxHeight: 'none',
                      maxWidth: 'none',
                      cursor: (state?.scale || 1) > 1 ? 'grab' : 'default'
                    }}
                    onError={() => setError('Failed to display image')}
                  />
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </div>
      );
    }

    if (isPdf) {
      return (
        <iframe
          src={previewUrl}
          className="w-full h-[600px] border border-gray-300 rounded"
          title="Passport Preview"
          onError={() => setError('Failed to display PDF preview')}
        />
      );
    }

    return (
      <div className="w-full h-[600px] border border-gray-300 rounded bg-gray-50 flex items-center justify-center">
        <div className="text-center p-6">
          <div className="text-4xl mb-4">📄</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Unsupported File Type
          </h3>
          <p className="text-gray-600 mb-4">
            File type: {contentType || 'Unknown'}
          </p>
          {onDownload && (
            <button
              onClick={onDownload}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Download File
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={className} style={{ height }}>
      <div className="mb-3">
        <h4 className="text-md font-semibold text-gray-800">
          Passport Document
          {contentType && (
            <span className="text-xs text-gray-500 ml-2">
              ({contentType})
            </span>
          )}
        </h4>
      </div>
      {renderPreview()}
    </div>
  );
}

export default PassportViewer;